import React, { useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, Save } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "feldolgozas_alatt", label: "Feldolgozás alatt" },
  { value: "reszben_megjott", label: "Részben megjött" },
  { value: "teljesitve", label: "Teljesítve" },
];

const normStatus = (s) => (STATUS_OPTIONS.some((o) => o.value === s) ? s : "feldolgozas_alatt");

export default function OrderFullEditModal({ order, onClose, onSaved }) {
  const [f, setF] = useState({
    user_email: order.user_email || "",
    user_company: order.user_company || "",
    user_tax_id: order.user_tax_id || "",
    ship_country: order.ship_country || "",
    ship_postal_code: order.ship_postal_code || "",
    ship_city: order.ship_city || "",
    ship_street: order.ship_street || "",
    phone: order.phone || "",
    contact_name: order.contact_name || "",
    po_number: order.po_number || "",
    status: normStatus(order.status),
    notes: order.notes || "",
  });
  const [items, setItems] = useState(
    (order.items || []).map((it) => ({
      sku: it.sku || "",
      name: it.name || "",
      brand: it.brand || "",
      quantity: it.quantity || 1,
      unit_price: it.unit_price ?? 0,
    }))
  );
  const [sendEmail, setSendEmail] = useState(false);
  const [saving, setSaving] = useState(false);

  const currency = (order.currency || "EUR").toUpperCase();
  const hufRate = Number(order.huf_rate || 0);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setItem = (i, patch) => setItems((arr) => arr.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const addItem = () => setItems((arr) => [...arr, { sku: "", name: "", brand: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (i) => setItems((arr) => arr.filter((_, j) => j !== i));

  const totalEur = useMemo(
    () => items.reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.quantity) || 0), 0),
    [items]
  );
  const totalHuf = currency === "HUF" && hufRate > 0 ? Math.round(totalEur * hufRate) : null;

  const save = async () => {
    if (items.length === 0) { toast.error("A rendelésnek legalább egy tételt tartalmaznia kell."); return; }
    for (const it of items) {
      if (!it.sku && !it.name) { toast.error("Minden tételnek legyen cikkszáma vagy neve."); return; }
      if (Number(it.quantity) < 1) { toast.error("A darabszám legalább 1 kell legyen."); return; }
      if (Number(it.unit_price) < 0) { toast.error("Az egységár nem lehet negatív."); return; }
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/full`, {
        ...f,
        items: items.map((it) => ({
          sku: it.sku,
          name: it.name,
          brand: it.brand,
          quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
          unit_price: Number(it.unit_price) || 0,
        })),
        send_email: sendEmail,
      });
      const em = data?.modify_email;
      if (em?.sent && em?.recipients?.length) {
        toast.success(`Rendelés mentve · E-mail: ${em.recipients.join(", ")}`);
      } else {
        toast.success("Rendelés mentve");
      }
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(15,23,42,0.55)" }}
      data-testid="order-full-edit-modal"
    >
      <div className="bg-white w-full max-w-[900px] my-6 shadow-2xl" style={{ borderRadius: 4 }}>
        <div className="section-title flex items-center justify-between" style={{ background: "linear-gradient(180deg, #C9A227 0%, #9C7B12 100%)" }}>
          <span>Rendelés szerkesztése — #{order.id.slice(0, 8).toUpperCase()}</span>
          <button onClick={onClose} style={{ color: "#fff" }} data-testid="full-edit-close"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Customer / billing */}
          <div>
            <div className="text-xs font-bold uppercase mb-2" style={{ color: "var(--brand)" }}>Vevő adatok</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="E-mail" value={f.user_email} onChange={(v) => set("user_email", v)} testid="edit-email" />
              <Field label="Cégnév" value={f.user_company} onChange={(v) => set("user_company", v)} testid="edit-company" />
              <Field label="Adószám" value={f.user_tax_id} onChange={(v) => set("user_tax_id", v)} testid="edit-tax" />
              <Field label="Kapcsolattartó" value={f.contact_name} onChange={(v) => set("contact_name", v)} testid="edit-contact" />
              <Field label="Telefon" value={f.phone} onChange={(v) => set("phone", v)} testid="edit-phone" />
              <Field label="PO szám" value={f.po_number} onChange={(v) => set("po_number", v)} testid="edit-po" />
            </div>
          </div>

          {/* Shipping */}
          <div>
            <div className="text-xs font-bold uppercase mb-2" style={{ color: "var(--brand)" }}>Szállítási cím</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Ország" value={f.ship_country} onChange={(v) => set("ship_country", v)} testid="edit-country" />
              <Field label="Irányítószám" value={f.ship_postal_code} onChange={(v) => set("ship_postal_code", v)} testid="edit-postal" />
              <Field label="Város" value={f.ship_city} onChange={(v) => set("ship_city", v)} testid="edit-city" />
              <Field label="Utca, házszám" value={f.ship_street} onChange={(v) => set("ship_street", v)} testid="edit-street" />
            </div>
          </div>

          {/* Status + notes */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>Státusz</label>
              <select className="input" value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="edit-status">
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>Vevői megjegyzés</label>
              <input className="input" value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="edit-notes" />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase" style={{ color: "var(--brand)" }}>Tételek</div>
              <button onClick={addItem} className="btn-primary !py-1 !px-2.5 text-xs" data-testid="edit-add-item">
                <Plus size={12} /> Új tétel
              </button>
            </div>
            <table className="table-b2b" style={{ border: "1px solid var(--border)" }} data-testid="edit-items-table">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Cikkszám</th>
                  <th>Megnevezés</th>
                  <th style={{ width: 90 }}>Márka</th>
                  <th style={{ width: 70 }}>Db</th>
                  <th style={{ width: 110 }}>Egységár (EUR)</th>
                  <th style={{ width: 110 }}>Sor összeg</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td><input className="input !py-1 !px-1.5 font-mono text-xs" value={it.sku} onChange={(e) => setItem(i, { sku: e.target.value })} data-testid={`edit-item-sku-${i}`} /></td>
                    <td><input className="input !py-1 !px-1.5 text-xs" value={it.name} onChange={(e) => setItem(i, { name: e.target.value })} data-testid={`edit-item-name-${i}`} /></td>
                    <td><input className="input !py-1 !px-1.5 text-xs" value={it.brand} onChange={(e) => setItem(i, { brand: e.target.value })} data-testid={`edit-item-brand-${i}`} /></td>
                    <td><input type="number" min={1} className="input !py-1 !px-1 text-center font-mono" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} data-testid={`edit-item-qty-${i}`} /></td>
                    <td><input type="number" min="0" step="0.01" className="input !py-1 !px-1.5 font-mono text-right" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} data-testid={`edit-item-price-${i}`} /></td>
                    <td className="text-right font-mono text-xs">{((Number(it.unit_price) || 0) * (Number(it.quantity) || 0)).toFixed(2)} €</td>
                    <td>
                      <button onClick={() => removeItem(i)} className="btn-danger !py-1 !px-2 text-xs" data-testid={`edit-item-remove-${i}`}>
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end mt-2 text-sm font-bold" data-testid="edit-total">
              Végösszeg: {totalEur.toFixed(2)} €{totalHuf !== null && <span className="ml-2" style={{ color: "var(--text-mute)" }}>({totalHuf.toLocaleString("hu-HU")} Ft)</span>}
            </div>
          </div>

          {/* Email + actions */}
          <div className="flex items-center justify-between gap-3 flex-wrap border-t pt-4" style={{ borderColor: "var(--border)" }}>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="edit-send-email" />
              „Módosítottuk a rendelését" e-mail küldése a vevőnek
            </label>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="btn-ghost" data-testid="edit-cancel">Mégse</button>
              <button onClick={save} disabled={saving} className="btn-yellow" data-testid="edit-save">
                <Save size={14} /> {saving ? "Mentés..." : "Mentés"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>{label}</label>
      <input className="input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
